import {useMutation, useQueryClient} from '@tanstack/react-query';
import {signOut} from 'aws-amplify/auth';
import {ConsoleLogger} from 'aws-amplify/utils';

import {safeClear} from '../../../utils/safeLocalforage';
import type {MutationUiOptions} from '../../../utils/mutationMeta';

const logger = new ConsoleLogger('useLogoutMutation');

/**
 * Déconnecte l'utilisateur.
 *
 * Remplace le thunk `logout` de `src/store/mainslice.ts`, dont le reducer
 * remettait l'état à zéro et vidait `localStorage` et `localforage`. Ici, on
 * vide le cache React Query (`queryClient.clear()`) : c'est lui qui détient
 * désormais l'utilisateur, ses équipes, ses brouillons et ses publications.
 *
 * Le nettoyage a lieu que la déconnexion réussisse ou non — comme le
 * `isAnyOf(logout.fulfilled, logout.rejected)` du slice : si Cognito refuse, on
 * ne garde surtout pas les données de la session à l'écran.
 */
export function useLogoutMutation(options: MutationUiOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async () => {
      logger.info('logout');
      return signOut();
    },
    onSettled: async () => {
      localStorage.clear();
      // Ne rejette jamais : le cache React Query doit être vidé quoi qu'il arrive.
      await safeClear();
      queryClient.clear();
    },
  });
}
